<template>
  <div class="mx-auto">
    <!-- Main Revenue Table -->
    <div v-if="!showDetails" class="bg-white shadow-md overflow-hidden">
      <table class="w-full">
        <thead class="bg-gray-100 border-b">
          <tr>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Institution Name</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Total Revenue(KES)</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Payouts Made(KES)</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Pending Payout(KES)</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Commission Retained(KES)</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="institution in institutionsRevenue" :key="institution.id" class="border-b hover:bg-gray-50">
            <td class="py-3 px-4">{{ institution?.institution?.name }}</td>
            <td class="py-3 px-4 text-left text-gray-600 font-semibold">{{ formatNumber(institution.total_revenue) }}</td>
            <td class="py-3 px-4 text-left text-gray-600 font-semibold">{{ formatNumber(institution.total_disbursed) }}</td>
            <td class="py-3 px-4 text-left text-gray-600 font-semibold">{{ formatNumber(institution.total_pending_payouts) }}</td>
            <td class="py-3 px-4 text-left text-gray-600 font-semibold">{{ formatNumber(institution.total_commission) }}</td>
            <td class="py-3 px-4">
              <button @click="viewDetails(institution)" class="bg-orange-400 text-white px-4 py-2 rounded hover:bg-orange-600">
                View Details
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="flex justify-center items-center my-8">
        <div class="flex space-x-2">
          <!-- Previous Button -->
          <button 
            @click="changeRevenuePage(courseCurrentPage - 1)" 
            :disabled="courseCurrentPage === 1"
            class="text-blue-900 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <!-- Page Numbers -->
          <button 
            v-for="page in courseTotalPages" 
            :key="page"
            @click="changeRevenuePage(page)"
            :class="{ 
              'bg-blue-900 text-white': courseCurrentPage === page, 
              'text-gray-600': courseCurrentPage !== page 
            }"
            class="px-3 py-1 rounded hover:bg-blue-100"
          >
            {{ page }}
          </button>

          <!-- Next Button -->
          <button 
            @click="changeRevenuePage(courseCurrentPage + 1)" 
            :disabled="courseCurrentPage === courseTotalPages"
            class="text-blue-900 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>

    <!-- Detailed Course Table -->
    <div v-else class="bg-white shadow-md overflow-hidden">
      <div class="flex justify-end items-center p-4 border-b">
        <button @click="goBack(selectedInstitution)" class="text-orange-400 hover:underline">Back to Institutions</button>
      </div>
      <table class="w-full">
        <thead class="bg-gray-100 border-b">
          <tr>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Course Name</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Revenue Generated(KES)</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Partially Paid Enrollments</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Fully Paid Enrollments</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="course in courseDetails" :key="course.course_id" class="border-b hover:bg-gray-50">
            <td class="py-3 px-4">{{ course.name }}</td>
            <td class="py-3 px-4">{{ formatNumber(course.total_amount_paid) }}</td>
            <td class="py-3 px-4">{{ course.partially_paid_enrollments }}</td>
            <td class="py-3 px-4">{{ course.fully_paid_enrollments }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination for Detailed Table -->
      <div class="flex justify-center items-center my-8">
        <div class="flex space-x-2">
          <!-- Previous Button -->
          <button 
            @click="changeCoursePage(courseCurrentPage - 1)" 
            :disabled="courseCurrentPage === 1"
            class="text-blue-900 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <!-- Page Numbers -->
          <button 
            v-for="page in courseTotalPages" 
            :key="page"
            @click="changeCoursePage(page)"
            :class="{ 
              'bg-blue-900 text-white': courseCurrentPage === page, 
              'text-gray-600': courseCurrentPage !== page 
            }"
            class="px-3 py-1 rounded hover:bg-blue-100"
          >
            {{ page }}
          </button>

          <!-- Next Button -->
          <button 
            @click="changeCoursePage(courseCurrentPage + 1)" 
            :disabled="courseCurrentPage === courseTotalPages"
            class="text-blue-900 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { InstitutionsRevenueAPI, InstitutionsAPI } from '@/services';
import { useToast } from 'vue-toastification';
import { setApiSystem } from '@/services/api';
import { useStore } from 'vuex';
import { computed } from 'vue';

export default {
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
  },
  setup() {
    const toast = useToast();
    const store = useStore();
    const isAuthenticated = computed(() => store.getters.isAuthenticated);

    return { toast, store, isAuthenticated };
  },
  data() {
    return {
      institutions: [],
      institutionsRevenue: [],
      individual_institution: 'total',
      institutionName: '',
      showDetails: false,
      selectedInstitution: null,
      courseDetails: [],
      courseCurrentPage: 1,
      courseTotalPages: 1,
      perPage: 10,
      totalItems: 0,
      loading: false,
    };
  },
  created() {
    if (!this.isAuthenticated) {
      this.toast.error("Please log in to the eLearning system.");
      this.$router.push('/login?system=eLearning');
      return;
    }
    setApiSystem('eLearning');
    this.fetchInstitutions();
    this.fetchInstitutionsRevenue();
  },
  watch: {
    searchQuery(newQuery) {
      this.fetchInstitutionsRevenue(newQuery);
    },
  },
  methods: {
    async fetchInstitutions() {
      try {
        this.loading = true;
        const response = await InstitutionsAPI.getAll();
        this.institutions = response.statistics || response.data.statistics;
        if (this.institutions) {
          this.$emit("update-statistics", this.institutions);
        }
      } catch (error) {
        this.toast.error(`Failed to fetch institutions: ${error.message}`);
        this.institutions = [];
      } finally {
        this.loading = false;
      }
    },
    async fetchInstitutionsRevenue(searchQuery = this.searchQuery, page = this.courseCurrentPage, perPage = this.perPage) {
      try {
        this.loading = true;
        const params = { search: searchQuery || undefined, page, per_page: perPage };
        const response = await InstitutionsRevenueAPI.getTotalInstitutionRevenue(params);
        this.institutionsRevenue = response.data.institution_revenues || response.institution_revenues;

        if (searchQuery && !response.data.filtered) {
          this.institutionsRevenue = this.institutionsRevenue.filter((inst) =>
            inst.institution?.name.toLowerCase().includes(searchQuery.toLowerCase())
          );
        }

        const stats = response.data.summary || response.summary;
        const meta = response.data.meta || response.meta;
        if (stats) this.$emit("update-statistics", stats);
        if (meta) {
          this.courseTotalPages = meta.total_pages;
          this.courseCurrentPage = meta.current_page;
          this.perPage = meta.per_page;
          this.totalItems = meta.total_items;
        }
      } catch (error) {
        this.toast.error(`Failed to fetch institutions revenue: ${error.message}`);
        this.institutionsRevenue = [];
      } finally {
        this.loading = false;
      }
    },
    changeRevenuePage(page) {
      if (page >= 1 && page <= this.courseTotalPages && page !== this.courseCurrentPage) {
        this.courseCurrentPage = page;
        this.fetchInstitutionsRevenue(this.searchQuery, this.courseCurrentPage, this.perPage);
      }
    },
    changeCoursePage(page) {
      if (page >= 1 && page <= this.courseTotalPages && page !== this.courseCurrentPage) {
        this.courseCurrentPage = page;
        this.viewDetails(this.selectedInstitution, this.courseCurrentPage, this.perPage);
      }
    },
    formatNumber(value) {
      return typeof value === 'number' ? value.toLocaleString('en-US') : value;
    },
    async viewDetails(institution, page = 1, perPage = 10) {
      this.selectedInstitution = institution;
      this.showDetails = true;
      this.individual_institution = 'individual';

      if (institution) {
        this.$emit("update-statistics", institution, this.individual_institution);
      }

      try {
        this.loading = true;
        const params = { page, per_page: perPage };
        const response = await InstitutionsRevenueAPI.getCoursesIncomForInstitution(institution.institution_id, params);
        this.courseDetails = response.data.courses || [];
        const total_stats = response.data.total_stats || response.total_stats;
        if (total_stats) this.$emit("update-statistics", total_stats);

        const meta = response.data.meta || response.meta;
        if (meta) {
          this.courseTotalPages = meta.total_pages;
          this.courseCurrentPage = meta.current_page;
          this.perPage = meta.per_page;
          this.totalItems = meta.total_items;
        }
      } catch (error) {
        this.toast.error(`Failed to fetch course details: ${error.message}`);
        this.courseDetails = [];
      } finally {
        this.loading = false;
      }
    },
    goBack(institution) {
      this.showDetails = false;
      this.selectedInstitution = null;
      this.courseDetails = [];
      this.courseCurrentPage = 1;
      this.individual_institution = 'total';
      if (institution) {
        this.$emit("update-statistics", institution, this.individual_institution);
      }
    },
  },
};
</script>